/**
 * Helpers para escolha automática de morada por defeito a partir de uma
 * lista de moradas legacy (`Trel_ClienteMorada`). Partilhados pelos 2
 * fluxos de checkout (MY + CRM Nova Encomenda).
 *
 * Convenção legacy IHTP:
 *   - `ID_TipoMorada`: 'R' = residência (envio), 'F' = facturação (pessoa
 *     particular), 'A' = facturação (empresa). Algumas moradas têm tipos
 *     auxiliares (geral, facturação extra, etc.).
 *   - `Principal`: 'S' significa que é a morada por defeito do tipo.
 *
 * Os endpoints MY (`shopBillingAddresses` / `shopDeliveryAddresses`) já
 * filtram por tipo no servidor; o CRM (`/encomendas/addresses`) devolve
 * todas e usamos estas funções para filtrar/escolher.
 */

/**
 * Retorna as moradas elegíveis para envio (residência).
 * Aceita também moradas sem tipo definido como fallback.
 */
export function shippingAddresses(list) {
  return (list || []).filter((a) => a.ID_TipoMorada === 'R' || !a.ID_TipoMorada);
}

/**
 * Retorna as moradas elegíveis para facturação ('F' particular ou 'A' empresa).
 */
export function billingAddresses(list) {
  return (list || []).filter((a) => a.ID_TipoMorada === 'F' || a.ID_TipoMorada === 'A');
}

/**
 * Auto-pick da morada de envio por defeito:
 *   1. Tipo 'R' + `Principal=S`
 *   2. Tipo 'R' (qualquer)
 *   3. `Principal=S` (qualquer tipo)
 *   4. Primeira da lista
 */
export function pickDefaultShipping(list) {
  const arr = list || [];
  return arr.find((a) => a.ID_TipoMorada === 'R' && a.Principal === 'S')
    || arr.find((a) => a.ID_TipoMorada === 'R')
    || arr.find((a) => a.Principal === 'S')
    || arr[0]
    || null;
}

/**
 * Auto-pick da morada de facturação por defeito:
 *   1. Tipo 'F'/'A' + `Principal=S`
 *   2. Tipo 'F'/'A' (qualquer)
 *   3. `Principal=S` (qualquer tipo)
 *   4. Primeira da lista
 */
export function pickDefaultBilling(list) {
  const arr = list || [];
  const isBilling = (a) => a.ID_TipoMorada === 'F' || a.ID_TipoMorada === 'A';
  return arr.find((a) => isBilling(a) && a.Principal === 'S')
    || arr.find(isBilling)
    || arr.find((a) => a.Principal === 'S')
    || arr[0]
    || null;
}

/**
 * Verifica se um ID de morada ainda existe na lista — útil para detectar
 * stale sessionStorage e forçar re-pick do default.
 */
export function addressStillExists(list, id) {
  if (!id) return false;
  return (list || []).some((a) =>
    String(a.ID_ClienteMorada ?? a.ID_Cliente_Morada) === String(id));
}

/** Devolve o NIF da morada seleccionada (ou string vazia). */
export function nifFromSelectedAddress(list, id) {
  const found = (list || []).find((a) =>
    String(a.ID_ClienteMorada ?? a.ID_Cliente_Morada) === String(id));
  return (found?.Nif ?? found?.NIF ?? '').toString().trim();
}
